import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Image,
  SimpleGrid,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Input,
  Spinner,
  Wrap,
  WrapItem,
  Stack,
  HStack,
  Tooltip,
} from "@chakra-ui/react";
import UuidPreview from "./UuidPreview";
import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import CodesetSelect from "./CodesetSelect";
import { useClocks } from "./hooks/useClocks";
import ClockCard from "./components/ClockCard";
import GRLCPlacementEditor from "./GRLCPlacementEditor";
import ContractInstanceEditor from "./ContractInstanceEditor";
import PlacingStepper from "./PlacingStepper";
import {
  HiOutlineCode,
  HiOutlineArrowNarrowLeft,
  HiOutlineDocumentText,
  HiOutlineMenu,
  HiOutlineStatusOnline,
  HiOutlineCog,
  HiOutlinePlus,
  HiOutlineTrash,
  HiX,
} from "react-icons/hi";

const ProductHomepage = () => {
  const { productCode } = useParams();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [product, setProduct] = useState(null);
  const { clocks, loading: clocksLoading } = useClocks();
  const [selectedGRLCPlacement, setSelectedGRLCPlacement] = useState(null);
  const [selectedContractInstance, setSelectedContractInstance] =
    useState(null);

  const {
    isOpen: isEditorOpen,
    onOpen: openEditor,
    onClose: closeEditor,
  } = useDisclosure();

  const {
    isOpen: isContractInstanceEditorOpen,
    onOpen: openContractInstanceEditor,
    onClose: closeContractInstanceEditor,
  } = useDisclosure();

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    placingStageCode: "",
    productCode,
  });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const contractsUrl = `https://poc.mrc.ai/${productCode}/api/contracts`;
  const productsUrl = `https://poc.mrc.ai/${productCode}/api/product`;

  const loadContracts = async () => {
    try {
      const res = await fetch(contractsUrl);
      const data = await res.json();
      setContracts(Array.isArray(data) ? data : data.contracts || []);
    } catch (err) {
      console.error("Failed to load contracts:", err);
      toast({ title: "Error loading contracts", status: "error" });
    }
  };

  const loadProduct = async () => {
    try {
      const res = await fetch(productsUrl);
      const data = await res.json();
      setProduct(data);
    } catch (err) {
      console.error("Failed to load product:", err);
      toast({ title: "Error loading product", status: "error" });
    }
  };

  useEffect(() => {
    loadContracts();
  }, [productCode]);

  useEffect(() => {
    loadProduct();
  }, []);

  const handleAdd = () => {
    setFormData({ name: "", type: "", placingStageCode: "", productCode });
    setSelected(null);
    onOpen();
  };

  const handleEdit = (contract) => {
    setSelected(contract);
    setFormData({
      name: contract.name || "",
      type: contract.type || "",
      placingStageCode: contract.placingStageCode || "",
      placingTransactionFunctionCode:
        contract.placingTransactionFunctionCode || "",
    });
    onOpen();
  };

  const handleEditContractInstance = (contract) => {
    setSelectedContractInstance(contract);
    openContractInstanceEditor();
  };

  const handlePlacementOpen = (contract) => {
    setSelectedGRLCPlacement(contract.grlcPlacement || {});
    openEditor();
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${contractsUrl}/${id}`, { method: "DELETE" });
      toast({ title: "Contract deleted", status: "info" });
      loadContracts();
    } catch (err) {
      toast({ title: "Error deleting contract", status: "error" });
    }
  };

  const handleSubmit = async () => {
    const method = selected ? "PUT" : "POST";
    const url = selected ? `${contractsUrl}/${selected._id}` : contractsUrl;
    const payload = selected ? { ...selected, ...formData } : formData;

    try {
      await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      toast({
        title: selected ? "Contract updated" : "Contract created",
        status: "success",
      });
      onClose();
      loadContracts();
    } catch (err) {
      toast({ title: "Error saving contract", status: "error" });
    }
  };

  const handleClose = () => {
    navigate(`/Poc`);
  };

  return (
    <Box>
      <Navbar />

      {/* Watermark background */}
      <Box position="relative" width="100%" minHeight="100vh">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        {/* Overlay content */}
        <Box position="relative" zIndex={1} p={10}>
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            <Flex mb={2}>
              <HStack spacing={6}>
                <Tooltip
                  label="Back to the Environment Homepage"
                  aria-label="Back To Environment tooltip">
                  <IconButton
                    icon={<HiOutlineArrowNarrowLeft />}
                    size="md"
                    onClick={handleClose}
                  />
                </Tooltip>
                <Heading size="lg">
                  {productCode.toUpperCase()} Product Dashboard
                </Heading>
                <Tooltip
                  label="Add Contract Instance"
                  aria-label="Add Contract Instance Tooltip">
                  <IconButton
                    icon={<HiOutlinePlus />}
                    size="md"
                    onClick={handleAdd}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            mb={2}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            {clocksLoading ? (
              <Spinner />
            ) : (
              <Wrap spacing={4}>
                {clocks.map((clock) => (
                  <ClockCard key={clock._id} clock={clock} />
                ))}
              </Wrap>
            )}
          </Box>

          <Box
            borderWidth="1px"
            borderRadius="md"
            p={4}
            mb={6}
            borderColor={"gray.500"}
            backgroundColor="whiteAlpha.800">
            <Flex mb={6}>
              <HStack spacing={6}>
                <Heading size="md">Contracts</Heading>
              </HStack>
            </Flex>

            <SimpleGrid columns={[1, 2, 3, 4, 5]} spacing={6}>
              {contracts.map((contract) => (
                <Flex
                  key={contract._id}
                  direction={{ base: "column", md: "row" }}
                  p={5}
                  borderWidth="1px"
                  borderRadius="lg"
                  shadow="md"
                  bg="white">
                  <Box flex="1" pr={{ base: 0, md: 4 }} mb={{ base: 4, md: 0 }}>
                    <HStack
                      align="center"
                      justify="flex-start"
                      spacing={1}>
                      <Tooltip
                        label="Contract Instance Header"
                        aria-label="Edit Contract Instance Header Tooltip">
                        <IconButton
                          icon={<HiOutlineMenu />}
                          size="md"
                          onClick={() =>
                            navigate(
                              `/product/${contract.productCode}/contract/${contract._id}/contract`
                            )
                          }
                          _hover={{ transform: "scale(1.1)" }}
                          transition="transform 0.2s ease"
                        />
                      </Tooltip>

                      <Tooltip
                        label={<UuidPreview uuid={contract._id} />}
                        hasArrow
                        placement="top"
                        openDelay={300}>
                        <Box width="fit-content">
                          <Heading
                            size="md"
                            width="auto"
                            whiteSpace="nowrap"
                            maxW="200px"
                            overflow="hidden"
                            textOverflow="ellipsis">
                            {contract.name}
                          </Heading>
                        </Box>
                      </Tooltip>
                    </HStack>

                    <SimpleGrid columns={5} spacing={4}></SimpleGrid>

                    <SimpleGrid>
                      <Box
                        borderWidth="1px"
                        borderRadius="md"
                        borderColor="gray.200">
                        <Tooltip
                          label="Edit Placement"
                          aria-label="Edit Placement Tooltip">
                          <IconButton
                            icon={<HiOutlineCode />}
                            size="md"
                            onClick={() =>
                              navigate(
                                `/product/${contract.productCode}/contract/${contract._id}/placement`
                              )
                            }
                            _hover={{ transform: "scale(1.1)" }}
                            transition="transform 0.2s ease"
                          />
                        </Tooltip>
                        <Tooltip
                          label="Create Standard Signal"
                          aria-label="Create Standard Signal Tooltip">
                          <IconButton
                            icon={<HiOutlineStatusOnline />}
                            size="md"
                            onClick={() =>
                              navigate(
                                `/product/${contract.productCode}/contract/${contract._id}/signal`
                              )
                            }
                            _hover={{ transform: "scale(1.1)" }}
                            transition="transform 0.2s ease"
                          />
                        </Tooltip>
                      </Box>
                    </SimpleGrid>

                    <SimpleGrid columns={1}>
                      <PlacingStepper
                        size="xs"
                        showSubSteps={false}
                        stageCode={contract.placingStageCode}
                        transactionCode={contract.placingTransactionCode}
                        defaultOrientation="vertical"
                      />
                    </SimpleGrid>

                    <Box display="flex" justifyContent="flex-end">
                      <IconButton
                        icon={<HiOutlineTrash />}
                        size="sm"
                        onClick={() => handleDelete(contract._id)}
                        _hover={{ transform: "scale(1.1)" }}
                        transition="transform 0.2s ease"
                      />
                    </Box>
                  </Box>
                </Flex>
              ))}
            </SimpleGrid>

            <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
              <ModalOverlay />
              <ModalContent
                maxW="600px"
                bg="white"
                borderRadius="xl"
                boxShadow="lg"
                border="1px solid"
                px={6}>
                <ModalHeader fontSize="xl" fontWeight="bold">
                  {selected ? "Edit Contract Header" : "Add Contract Header"}
                </ModalHeader>
                <ModalBody>
                  <Box
                    as="form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}>
                    <Stack spacing={4}>
                      <Input
                        placeholder="Contract Name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                      />
                      <CodesetSelect
                        label="Placing Stage"
                        codesetCode="A83"
                        value={
                          formData.placingStageCode
                            ? {
                                code: formData.placingStageCode,
                                description: "",
                              }
                            : null
                        }
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            placingStageCode: value.code,
                          })
                        }
                      />
                      <CodesetSelect
                        label="Placing Transaction"
                        codesetCode="1225"
                        value={
                          formData.placingTransactionFunctionCode
                            ? {
                                code: formData.placingTransactionFunctionCode,
                                description: "",
                              }
                            : null
                        }
                        onChange={(value) =>
                          setFormData({
                            ...formData,
                            placingTransactionFunctionCode: value.code,
                          })
                        }
                      />
                    </Stack>
                  </Box>
                </ModalBody>
                <ModalFooter>
                  <ModalCloseButton />
                  <Button onClick={handleSubmit}>
                    {selected ? "Update" : "Create"}
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductHomepage;
